import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useRevertTablesStore } from "@/store/revert-tables-store";

/** Surfaces alternative table picks when reactivating a NO_SHOW /
 *  CANCELLED / COMPLETED reservation finds the original mesas taken
 *  by another booking. Operator chooses one and the revert endpoint
 *  retries with ``{tables: [...]}`` in the body. */
export function RevertTablesDialog() {
  const pending = useRevertTablesStore((s) => s.pending);
  const close = useRevertTablesStore((s) => s.close);

  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  const ctx = pending?.context;
  const suggestions = ctx?.suggestions ?? [];

  return (
    <AlertDialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedIdx(null);
          close(null);
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mesas originales no disponibles</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block">
              Las mesas que tenía <strong>{ctx?.reservationLabel}</strong> ya están ocupadas. Estas
              opciones siguen libres en ese horario — elige una para continuar:
            </span>
            {suggestions.length === 0 ? (
              <span className="mt-3 block text-sm text-muted-foreground">
                No hay opciones libres en ese horario. Cancela y resuelve antes de revertir.
              </span>
            ) : (
              <ul className="mt-3 flex flex-col gap-2 text-foreground">
                {suggestions.map((s, idx) => {
                  const moveCount = s.moves.length;
                  const checked = selectedIdx === idx;
                  return (
                    <li key={s.table_ids.join("|")}>
                      <label
                        className={`flex w-full cursor-pointer items-start gap-2 rounded-md border p-2 text-sm ${
                          checked ? "border-primary bg-muted" : "border-border"
                        }`}
                      >
                        <input
                          type="radio"
                          name="revert-pick"
                          checked={checked}
                          onChange={() => setSelectedIdx(idx)}
                          className="mt-0.5"
                        />
                        <span className="flex-1">
                          <strong>{s.table_names.join(" + ")}</strong>
                          <span className="ml-1 text-xs text-muted-foreground">
                            (capacidad {s.total_capacity}
                            {s.wasted_capacity > 0 ? `, sobran ${s.wasted_capacity}` : ""})
                          </span>
                          {moveCount > 0 ? (
                            <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 px-1.5 text-xs text-amber-900">
                              ↻ mueve {moveCount}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              setSelectedIdx(null);
              close(null);
            }}
          >
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={selectedIdx === null}
            onClick={() => {
              if (selectedIdx === null) return;
              const pick = suggestions[selectedIdx];
              setSelectedIdx(null);
              close({ tables: pick.table_ids, moves: pick.moves });
            }}
          >
            Usar estas mesas
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
